import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { ConsentProvider } from "@/lib/consent";
import { EditModeProvider } from "@/lib/editMode";
import { Toaster } from "sonner";
import { WhatsAppFloat } from "@/components/WhatsApp";
import ScrollToTop from "@/components/ScrollToTop";
import ClinicalNotApprovedBanner from "@/components/ClinicalNotApprovedBanner";
import Landing from "@/pages/Landing";
import Team from "@/pages/Team";
import Services from "@/pages/Services";
import Prices from "@/pages/Prices";
import CheckoutSummary from "@/pages/CheckoutSummary";
import SecuritySettings from "@/pages/SecuritySettings";
import AILibrary from "@/pages/AILibrary";
import VerifyEmail from "@/pages/VerifyEmail";
import AdminDsr from "@/pages/AdminDsr";
import AdminCompliance from "@/pages/AdminCompliance";
import AdminBreaches from "@/pages/AdminBreaches";
import AdminAlerts from "@/pages/AdminAlerts";
import AdminAccess from "@/pages/AdminAccess";
import AdminTrust from "@/pages/AdminTrust";
import AdminReadiness from "@/pages/AdminReadiness";
import Trust from "@/pages/Trust";
import Testimonials from "@/pages/Testimonials";
import Contact from "@/pages/Contact";
import Resources from "@/pages/Resources";
import ServiceFinderPage from "@/pages/ServiceFinderPage";
import Community from "@/pages/Community";
import CommunityLanding from "@/pages/CommunityLanding";
import { SiteSettingsProvider } from "@/lib/siteSettings";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Onboarding from "@/pages/Onboarding";
import BeforeFirstVisit from "@/pages/BeforeFirstVisit";
import ClinicianProposals from "@/pages/ClinicianProposals";
import ClinicianCoachQuality from "@/pages/ClinicianCoachQuality";
import Privacy from "@/pages/Privacy";
import PatientHome from "@/pages/PatientHome";
import PatientPlan from "@/pages/PatientPlan";
import PatientProgress from "@/pages/PatientProgress";
import PatientAppointments from "@/pages/PatientAppointments";
import Messages from "@/pages/Messages";
import Learn from "@/pages/Learn";
import Packages from "@/pages/Packages";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import MfaSetup from "@/pages/MfaSetup";
import AuthCallback from "@/pages/AuthCallback";
import PatientVideos from "@/pages/PatientVideos";
import PatientWearables from "@/pages/PatientWearables";
import PatientIntake from "@/pages/PatientIntake";
import CPDPage from "@/pages/CPDPage";
import ClinicianReflections from "@/pages/ClinicianReflections";
import ClinicianHome from "@/pages/ClinicianHome";
import ClinicianPatient from "@/pages/ClinicianPatient";
import ClinicianSchedule from "@/pages/ClinicianSchedule";
import ClinicianAvailability from "@/pages/ClinicianAvailability";
import ClinicianLibrary from "@/pages/ClinicianLibrary";
import ClinicianPeriodisation from "@/pages/ClinicianPeriodisation";
import ClinicianReviewCenter from "@/pages/ClinicianReviewCenter";
import ClinicianCalendar from "@/pages/ClinicianCalendar";
import ClinicianMeetings from "@/pages/ClinicianMeetings";
import ClinicianAssessment from "@/pages/ClinicianAssessment";
import ClinicianIntakes from "@/pages/ClinicianIntakes";
import ClinicianTeam from "@/pages/ClinicianTeam";
import ClinicianForms from "@/pages/ClinicianForms";
import RehabTemplates from "@/pages/RehabTemplates";
import PatientForms from "@/pages/PatientForms";
import AdminHome from "@/pages/AdminHome";
import AdminStaff from "@/pages/AdminStaff";
import AdminServices from "@/pages/AdminServices";
import AdminPackages from "@/pages/AdminPackages";
import PatientProgrammes from "@/pages/PatientProgrammes";
import ProgrammeScreening from "@/pages/ProgrammeScreening";
import AdminIntegrations from "@/pages/AdminIntegrations";
import AdminAudit from "@/pages/AdminAudit";
import AdminDiagnostics from "@/pages/AdminDiagnostics";
import AdminCms from "@/pages/AdminCms";
import AdminUsers from "@/pages/AdminUsers";
import AdminPayments from "@/pages/AdminPayments";
import AdminSiteContact from "@/pages/AdminSiteContact";
import PatientCoach from "@/pages/PatientCoach";
import PatientProfile from "@/pages/PatientProfile";
import ClinicianMonitoring from "@/pages/ClinicianMonitoring";
import ClinicianInquiries from "@/pages/ClinicianInquiries";
import ForcePasswordChange from "@/pages/ForcePasswordChange";
import AnkleProgramme from "@/pages/AnkleProgramme";
import AnkleFinder from "@/pages/AnkleFinder";
import ClinicianPersonalisationReviews from "@/pages/ClinicianPersonalisationReviews";
import ClinicianPhysicalTests from "@/pages/ClinicianPhysicalTests";

function Protected({ role, children, allowMfaSetup = false, need = null }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-zinc-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Force-reset gate for seeded/onboarded staff with a temp password.
  if (user.must_change_password) return <ForcePasswordChange />;
  // Mandatory MFA for staff — clinician/admin without MFA cannot use the
  // app until they've enrolled an authenticator. `allowMfaSetup` opts the
  // MFA setup route itself out of the bounce so the wizard is reachable.
  if (!allowMfaSetup && user.mfa_setup_required && loc.pathname !== "/mfa/setup") {
    return <Navigate to="/mfa/setup" replace />;
  }
  if (role) {
    const roles = Array.isArray(role) ? role : [role];
    const ok = roles.includes(user.role) || (roles.includes("admin") && user.is_admin);
    if (!ok) {
      const home = user.role === "patient" ? "/app" : user.role === "clinician" ? "/clinician" : "/admin";
      return <Navigate to={home} replace />;
    }
  }
  // Feature-specific gating: a patient may only reach a clinical route when the
  // matching capability isn't explicitly false. Today/Messages/booking etc. pass
  // no `need` and stay open to every patient. Fail-open if entitlements absent.
  if (need && user.role === "patient") {
    const ent = user.entitlements || {};
    if (ent[need] === false) return <Navigate to="/app" replace />;
  }
  return children;
}

function PublicWhatsApp() {
  const loc = useLocation();
  const p = loc.pathname;
  const authArea = p.startsWith("/app") || p.startsWith("/clinician") || p.startsWith("/admin");
  if (authArea) return null;
  return <WhatsAppFloat />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ConsentProvider>
          <BrowserRouter>
          <SiteSettingsProvider>
          <EditModeProvider>
          <ScrollToTop />
          <ClinicalNotApprovedBanner />
          <Toaster position="top-right" toastOptions={{ className: "fl-card", style: { background: "var(--fl-bg-2)", border: "1px solid var(--fl-border)", color: "var(--fl-text)" } }} />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/team" element={<Team />} />
          <Route path="/services" element={<Services />} />
          <Route path="/prices" element={<Prices />} />
          <Route path="/checkout/summary" element={<CheckoutSummary />} />
          <Route path="/security-settings" element={<SecuritySettings />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/admin/dsr" element={<Protected role="admin"><AdminDsr /></Protected>} />
          <Route path="/admin/payments" element={<Protected role="admin"><AdminPayments /></Protected>} />
          <Route path="/admin/compliance" element={<Protected role="admin"><AdminCompliance /></Protected>} />
          <Route path="/admin/breaches" element={<Protected role="admin"><AdminBreaches /></Protected>} />
          <Route path="/admin/alerts" element={<Protected role="admin"><AdminAlerts /></Protected>} />
          <Route path="/admin/access" element={<Protected role="admin"><AdminAccess /></Protected>} />
          <Route path="/admin/trust" element={<Protected role="admin"><AdminTrust /></Protected>} />
          <Route path="/admin/readiness" element={<Protected role="admin"><AdminReadiness /></Protected>} />
          <Route path="/trust" element={<Trust />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/service-finder" element={<ServiceFinderPage />} />
          <Route path="/community" element={<CommunityLanding />} />
          <Route path="/login" element={<Login />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/register" element={<Register />} />
          <Route path="/onboarding" element={<Protected role="patient"><Onboarding /></Protected>} />
          <Route path="/before-first-visit" element={<BeforeFirstVisit />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/mfa/setup" element={<Protected allowMfaSetup><MfaSetup /></Protected>} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/app/checkout/success" element={<Protected><CheckoutSuccess /></Protected>} />
          <Route path="/app/checkout/cancel" element={<Protected><Packages /></Protected>} />

          <Route path="/app" element={<Protected role="patient"><PatientHome /></Protected>} />
          <Route path="/app/plan" element={<Protected role="patient" need="can_access_my_plan"><PatientPlan /></Protected>} />
          <Route path="/app/progress" element={<Protected role="patient" need="can_access_metrics"><PatientProgress /></Protected>} />
          <Route path="/app/appointments" element={<Protected role="patient"><PatientAppointments /></Protected>} />
          <Route path="/app/messages" element={<Protected role="patient"><Messages /></Protected>} />
          <Route path="/app/learn" element={<Protected role="patient"><Learn /></Protected>} />
          <Route path="/app/videos" element={<Protected role="patient"><PatientVideos /></Protected>} />
          <Route path="/app/wearables" element={<Protected role="patient" need="can_access_metrics"><PatientWearables /></Protected>} />
          <Route path="/app/intake" element={<Protected role="patient"><PatientIntake /></Protected>} />
          <Route path="/app/intake/:id" element={<Protected role="patient"><PatientIntake /></Protected>} />
          <Route path="/app/packages" element={<Protected role="patient"><Packages /></Protected>} />
          <Route path="/app/programmes" element={<Protected role="patient"><PatientProgrammes /></Protected>} />
          <Route path="/programmes/check-suitability/:id" element={<ProgrammeScreening />} />
          <Route path="/online-programmes/ankle" element={<AnkleProgramme />} />
          <Route path="/online-programmes/ankle/finder" element={<AnkleFinder />} />
          <Route path="/app/coach" element={<Protected role="patient" need="can_check_in"><PatientCoach /></Protected>} />
          <Route path="/app/community" element={<Protected role="patient"><Community /></Protected>} />
          <Route path="/app/profile" element={<Protected role="patient"><PatientProfile /></Protected>} />

          <Route path="/clinician" element={<Protected role="clinician"><ClinicianHome /></Protected>} />
          <Route path="/clinician/monitoring" element={<Protected role={["clinician","admin"]}><ClinicianMonitoring /></Protected>} />
          <Route path="/clinician/proposals" element={<Protected role="clinician"><ClinicianProposals /></Protected>} />
          <Route path="/clinician/coach-quality" element={<Protected role="clinician"><ClinicianCoachQuality /></Protected>} />
          <Route path="/clinician/community" element={<Protected role={["clinician","admin"]}><Community /></Protected>} />
          <Route path="/clinician/inquiries" element={<Protected role={["clinician","admin"]}><ClinicianInquiries /></Protected>} />
          <Route path="/clinician/patient/:id" element={<Protected role="clinician"><ClinicianPatient /></Protected>} />
          <Route path="/clinician/appointments" element={<Protected role="clinician"><ClinicianSchedule /></Protected>} />
          <Route path="/clinician/availability" element={<Protected role={["clinician","admin"]}><ClinicianAvailability /></Protected>} />
          <Route path="/clinician/assessment" element={<Protected role={["clinician","admin"]}><ClinicianAssessment /></Protected>} />
          <Route path="/clinician/notes" element={<Protected role={["clinician","admin"]}><ClinicianAssessment /></Protected>} />
          <Route path="/clinician/intakes" element={<Protected role={["clinician","admin"]}><ClinicianIntakes /></Protected>} />
          <Route path="/clinician/messages" element={<Protected role="clinician"><Messages /></Protected>} />
          <Route path="/clinician/library" element={<Protected role={["clinician","admin"]}><ClinicianLibrary /></Protected>} />
          <Route path="/clinician/periodisation" element={<Protected role={["clinician","admin"]}><ClinicianPeriodisation /></Protected>} />
          <Route path="/clinician/ai-library" element={<Protected role={["clinician","admin"]}><AILibrary /></Protected>} />
          <Route path="/clinician/review-center" element={<Protected role="clinician"><ClinicianReviewCenter /></Protected>} />
          <Route path="/clinician/personalisation-reviews" element={<Protected role={["clinician","admin"]}><ClinicianPersonalisationReviews /></Protected>} />
          <Route path="/clinician/cpd" element={<Protected role={["clinician","admin"]}><CPDPage /></Protected>} />
          <Route path="/clinician/reflections" element={<Protected role={["clinician","admin"]}><ClinicianReflections /></Protected>} />
          <Route path="/clinician/calendar" element={<Protected role={["clinician","admin"]}><ClinicianCalendar /></Protected>} />
          <Route path="/clinician/meetings" element={<Protected role={["clinician","admin"]}><ClinicianMeetings /></Protected>} />
          <Route path="/clinician/team" element={<Protected role={["clinician","admin"]}><ClinicianTeam /></Protected>} />
          <Route path="/clinician/forms" element={<Protected role={["clinician","admin"]}><ClinicianForms /></Protected>} />
          <Route path="/clinician/templates" element={<Protected role={["clinician","admin"]}><RehabTemplates /></Protected>} />
          <Route path="/clinician/physical-tests" element={<Protected role={["clinician","admin"]}><ClinicianPhysicalTests /></Protected>} />
          <Route path="/admin/templates" element={<Protected role="admin"><RehabTemplates /></Protected>} />
          <Route path="/app/forms" element={<Protected role="patient" need="can_access_forms"><PatientForms /></Protected>} />
          <Route path="/app/forms/:assignmentId" element={<Protected role="patient" need="can_access_forms"><PatientForms /></Protected>} />

          <Route path="/admin" element={<Protected role="admin"><AdminHome /></Protected>} />
          <Route path="/admin/staff" element={<Protected role="admin"><AdminStaff /></Protected>} />
          <Route path="/admin/services" element={<Protected role="admin"><AdminServices /></Protected>} />
          <Route path="/admin/packages" element={<Protected role="admin"><AdminPackages /></Protected>} />
          <Route path="/admin/site-contact" element={<Protected role="admin"><AdminSiteContact /></Protected>} />
          <Route path="/admin/integrations" element={<Protected role="admin"><AdminIntegrations /></Protected>} />
          <Route path="/admin/audit" element={<Protected role="admin"><AdminAudit /></Protected>} />
          <Route path="/admin/cms" element={<Protected role="admin"><AdminCms /></Protected>} />
          <Route path="/admin/users" element={<Protected role="admin"><AdminUsers /></Protected>} />
          <Route path="/admin/diagnostics" element={<Protected role="admin"><AdminDiagnostics /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <PublicWhatsApp />
        </EditModeProvider>
          </SiteSettingsProvider>
      </BrowserRouter>
      </ConsentProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
export default App;
